import { WalletClient, SendTransactionParameters, Chain, createPublicClient, http, TransactionReceipt } from "viem";

import { EVMChainConfig, TransactionRequest } from "../types";

const getChainFromChainConfig = (chainConfig: EVMChainConfig): Chain => {
    return {
        blockExplorers: {
            default: { name: chainConfig.network, url: chainConfig.explorer ?? "" },
        },
        id: Number(chainConfig.chain_id),
        name: chainConfig.network,
        nativeCurrency: chainConfig.native_currency,
        rpcUrls: {
            default: { http: [chainConfig.rpc ?? ""] },
        },
    };
};

const getSendTransactionParametersFromTransactionRequest = (
    transaction: TransactionRequest,
    chainConfig: EVMChainConfig
): SendTransactionParameters => {
    return {
        account: transaction.from as `0x${string}`,
        to: transaction.to as `0x${string}`,
        value: transaction.value,
        data: transaction.data as `0x${string}`,
        gas: transaction.gasLimit,
        nonce: transaction.nonce,
        chain: getChainFromChainConfig(chainConfig),
    };
};

export const addChain = async (wallet: WalletClient, chainConfig: EVMChainConfig) => {
    const chain = getChainFromChainConfig(chainConfig);
    if (chain) {
        await wallet.addChain({ chain });
    }
};

export const switchChain = async (chainId: number, wallet: WalletClient, chainConfig?: EVMChainConfig) => {
    try {
        await wallet.switchChain({ id: chainId });
    } catch (switchError) {
        console.error("Error switching chain", switchError);
        if (!chainConfig) {
            throw switchError;
        }

        try {
            // Add the chain to the wallet and try again
            await addChain(wallet, chainConfig);
            await wallet.switchChain({ id: chainId });
        } catch (addChainError) {
            console.error("Error adding chain", addChainError);
            throw addChainError;
        }
    }
};

export const sendTransaction = async ({
    walletClient,
    transaction,
    chainConfig,
}: {
    walletClient: WalletClient;
    transaction: TransactionRequest;
    chainConfig: EVMChainConfig;
}): Promise<TransactionReceipt> => {
    const publicClient = createPublicClient({
        chain: getChainFromChainConfig(chainConfig),
        transport: http(chainConfig.rpc),
    });
    const txHash = await walletClient.sendTransaction(
        getSendTransactionParametersFromTransactionRequest(transaction, chainConfig)
    );
    return await publicClient.waitForTransactionReceipt({ hash: txHash });
};
